import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { videosApi } from "@/api/videos.ts";
import {
  PageContainer, TfCard, Btn, Icon, StatusPill, FormatBadge, TfSpinner, EmptyState,
} from "@/components/v2/primitives.tsx";
import { DirectorBrief } from "@/components/v2/DirectorBrief.tsx";
import { EngineReview } from "@/components/engine/EngineReview.tsx";

function LegacyVideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["video", id],
    queryFn: () => videosApi.get(id!),
    enabled: !!id,
  });

  const [showPublish, setShowPublish] = useState(false);

  const selectThumbMutation = useMutation({
    mutationFn: (url: string) => videosApi.selectThumbnail(id!, url),
    onSuccess: () => { useQueryClient().invalidateQueries({ queryKey: ["video", id] }); },
  });

  const video = data?.video;
  const segments = data?.segments ?? [];

  const deleteMutation = useMutation({
    mutationFn: () => videosApi.delete(id!),
    onSuccess: () => navigate("/videos"),
  });

  if (isLoading) return <PageContainer><div style={{ display: "flex", justifyContent: "center", padding: 80 }}><TfSpinner size={28} /></div></PageContainer>;
  if (isError || !video) return <PageContainer><EmptyState icon="x" title="Video not found" action={<Btn onClick={() => navigate("/videos")}>Back</Btn>} /></PageContainer>;

  const title = video.scriptText?.split("\n")[0]?.slice(0, 100) ?? "Untitled video";
  const isReady = video.status === "complete";
  const isPublished = video.status === "published";

  return (
    <PageContainer dense maxWidth={1200}>
      <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Left: player + description */}
        <div style={{ flex: 1, minWidth: 320 }}>
          {/* Video player / thumbnail */}
          <TfCard padding={0} style={{ overflow: "hidden", marginBottom: 16 }}>
            {video.finalVideoUrl ? (
              <video
                src={video.finalVideoUrl}
                controls
                poster={video.selectedThumbnailUrl || video.thumbnailUrls?.[0] || undefined}
                style={{ width: "100%", display: "block", aspectRatio: video.format === "shorts" ? "9/16" : "16/9", maxHeight: 480, background: "#000" }}
              />
            ) : (
              <div style={{
                aspectRatio: video.format === "shorts" ? "9/16" : "16/9",
                maxHeight: 480,
                background: (video.selectedThumbnailUrl || video.thumbnailUrls?.[0])
                  ? `url(${video.selectedThumbnailUrl || video.thumbnailUrls?.[0]}) center/cover` : "var(--tf-bg-2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span className="tf-muted" style={{ fontSize: 14 }}>No video file yet</span>
              </div>
            )}
          </TfCard>

          {/* Title + status */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, lineHeight: 1.35 }}>{title}</h1>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                <StatusPill status={video.status} />
                <FormatBadge format={video.format} />
                <span className="tf-muted" style={{ fontSize: 12 }}>· Created {new Date(video.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Script text */}
          {video.scriptText && (
            <TfCard padding={16} style={{ marginBottom: 16 }}>
              <div className="tf-label" style={{ marginBottom: 8 }}>Script</div>
              <div style={{ fontSize: 13, lineHeight: 1.7, color: "var(--tf-fg)", whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>
                {video.scriptText}
              </div>
            </TfCard>
          )}

          {/* Storyboard thumbnails */}
          {segments.length > 0 && (
            <TfCard padding={16}>
              <div className="tf-label" style={{ marginBottom: 10 }}>Storyboard ({segments.length} frames)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8 }}>
                {segments.filter(s => s.imageUrl).map((seg, i) => (
                  <div key={seg.id} style={{
                    aspectRatio: video.format === "shorts" ? "9/16" : "16/9",
                    background: `url(${seg.imageUrl}) center/cover`,
                    borderRadius: 6, border: "1px solid var(--tf-rule)",
                  }} title={`Frame ${i + 1}: ${seg.text?.slice(0, 60)}`} />
                ))}
              </div>
            </TfCard>
          )}

          {/* AI Director Strategy */}
          {(video.contentBrief || video.creativeDirection) && (
            <div style={{ marginTop: 16 }}>
              <DirectorBrief
                contentBrief={video.contentBrief as any}
                creativeDirection={video.creativeDirection as any}
              />
            </div>
          )}
        </div>

        {/* Right sidebar: actions + specs */}
        <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12 }} className="tf-mobile-hide">
          {/* Actions */}
          <TfCard padding={16}>
            <div className="tf-label" style={{ marginBottom: 12 }}>Actions</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {video.finalVideoUrl && (
                <Btn fullWidth icon="download" variant="primary" onClick={() => { window.open(video.finalVideoUrl!, "_blank"); }}>
                  Download MP4
                </Btn>
              )}
              {isReady && (
                <Btn fullWidth icon="youtube" variant="ghost" onClick={() => setShowPublish(true)}>
                  Publish to YouTube
                </Btn>
              )}
              {isPublished && video.youtubeVideoId && (
                <Btn fullWidth icon="youtube" variant="ghost"
                  onClick={() => window.open(`https://www.youtube.com/watch?v=${video.youtubeVideoId}`, "_blank")}>
                  View on YouTube
                </Btn>
              )}
              {/* (TF3-3) "Edit script", "Edit storyboard" and "Video settings" went with the legacy pipeline */}
            </div>
          </TfCard>

          {/* Specs */}
          <TfCard padding={14}>
            <div className="tf-label" style={{ marginBottom: 10 }}>Details</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
              <Row label="Status" value={video.status.replace(/_/g, " ")} />
              <Row label="Format" value={video.format === "shorts" ? "Short (9:16)" : "Long-form (16:9)"} />
              <Row label="Preset" value={video.presetId} />
              <Row label="Credits used" value={String(video.creditsUsed)} />
              <Row label="Segments" value={String(segments.length)} />
              {video.captionFont && <Row label="Caption font" value={video.captionFont} />}
              <Row label="Created" value={new Date(video.createdAt).toLocaleString()} />
              <Row label="Updated" value={new Date(video.updatedAt).toLocaleString()} />
            </div>
          </TfCard>

          {/* Thumbnails */}
          {video.thumbnailUrls && video.thumbnailUrls.length > 0 && (() => {
            const meta = video.generatedMetadata as { thumbnailPrompts?: Array<{ label?: string; strategy?: string; textOverlay?: string }> } | null;
            const thumbPrompts = meta?.thumbnailPrompts ?? [];
            return (
              <TfCard padding={14}>
                <div className="tf-label" style={{ marginBottom: 10 }}>Thumbnail options</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {video.thumbnailUrls.map((url, i) => (
                    <div key={i} onClick={() => selectThumbMutation.mutate(url)} style={{
                      borderRadius: 8, overflow: "hidden", cursor: "pointer",
                      border: url === video.selectedThumbnailUrl ? "2px solid var(--tf-accent)" : "1px solid var(--tf-rule)",
                    }}>
                      <div style={{
                        aspectRatio: video.format === "shorts" ? "9/16" : "16/9",
                        background: `url(${url}) center/cover`,
                        maxHeight: 120,
                      }} />
                      {thumbPrompts[i] && (
                        <div style={{ padding: "6px 10px", background: "var(--tf-surface)", borderTop: "1px solid var(--tf-rule)" }}>
                          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>
                            {thumbPrompts[i].label || `Option ${i + 1}`}
                          </div>
                          {thumbPrompts[i].textOverlay && (
                            <div className="tf-muted" style={{ fontSize: 10, marginTop: 2 }}>
                              Overlay: "{thumbPrompts[i].textOverlay}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </TfCard>
            );
          })()}

          {/* Danger zone */}
          <TfCard padding={14} style={{ borderColor: "var(--tf-danger)" }}>
            <Btn fullWidth variant="danger" icon="trash"
              onClick={() => { if (window.confirm("Delete this video? This cannot be undone.")) deleteMutation.mutate(); }}
              loading={deleteMutation.isPending}>
              Delete video
            </Btn>
          </TfCard>
        </div>
      </div>

      {/* Publish modal */}
      {showPublish && <PublishModal videoId={id!} metadata={video.generatedMetadata as any} onClose={() => setShowPublish(false)} />}
    </PageContainer>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span className="tf-muted">{label}</span>
      <span style={{ fontWeight: 500, textTransform: "capitalize" }}>{value}</span>
    </div>
  );
}

function PublishModal({ videoId, metadata, onClose }: {
  videoId: string;
  metadata?: { titles?: string[]; description?: string; tags?: string[] } | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const suggestedTitles = metadata?.titles ?? [];
  const [title, setTitle] = useState(suggestedTitles[0] ?? "");
  const [description, setDescription] = useState(metadata?.description ?? "");
  const [channelId, setChannelId] = useState("");
  const tags = metadata?.tags ?? [];

  const publishMutation = useMutation({
    mutationFn: () => videosApi.publish(videoId, {
      youtubeChannelId: channelId,
      title,
      description,
      tags,
      categoryId: 22,
      aiDisclosure: true,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["video", videoId] }); onClose(); },
  });

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 80, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--tf-bg)", borderRadius: 12, border: "1px solid var(--tf-rule)",
        boxShadow: "var(--tf-shadow-md)", width: "min(600px, 100%)", padding: 24, maxHeight: "90vh", overflowY: "auto",
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 16px" }}>Publish to YouTube</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>YouTube Channel ID</label>
            <input className="tf-input" value={channelId} onChange={e => setChannelId(e.target.value)}
              placeholder="UC..." style={{ width: "100%" }} />
          </div>

          {/* Title with AI suggestions */}
          <div>
            <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Title</label>
            <input className="tf-input" value={title} onChange={e => setTitle(e.target.value)}
              maxLength={100} placeholder="Video title" style={{ width: "100%" }} />
            {suggestedTitles.length > 1 && (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="tf-muted" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>AI suggestions</span>
                {suggestedTitles.map((t, i) => (
                  <button key={i} type="button" onClick={() => setTitle(t)} style={{
                    padding: "6px 10px", borderRadius: 6, cursor: "pointer", textAlign: "left",
                    background: title === t ? "var(--tf-accent-soft)" : "var(--tf-surface)",
                    border: "1px solid " + (title === t ? "var(--tf-accent)" : "var(--tf-rule)"),
                    color: "var(--tf-fg)", fontSize: 12, fontFamily: "inherit",
                  }}>
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Description</label>
            <textarea className="tf-input" value={description} onChange={e => setDescription(e.target.value)}
              rows={4} placeholder="Optional description…" />
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <label className="tf-label" style={{ display: "block", marginBottom: 6 }}>Tags (auto-generated)</label>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {tags.map((tag, i) => (
                  <span key={i} style={{
                    padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 500,
                    background: "var(--tf-surface)", color: "var(--tf-fg-muted)", border: "1px solid var(--tf-rule)",
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontSize: 12, color: "var(--tf-fg-muted)", display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="shield" size={14} /> AI disclosure label will be added automatically
          </div>
          {publishMutation.error && <p style={{ color: "var(--tf-danger)", fontSize: 13 }}>{(publishMutation.error as any)?.message ?? "Publish failed"}</p>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 8 }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn icon="youtube" onClick={() => publishMutation.mutate()}
              disabled={!channelId || !title} loading={publishMutation.isPending}>
              Publish
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Session 4 K2: a video made by the engine (`runId` set) is reviewed from its run — EngineReview reads the run's
 * filmstrip and every action is a gate decision. A legacy video (no run) keeps this page exactly as it was.
 */
export function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery({ queryKey: ["video", id], queryFn: () => videosApi.get(id!), enabled: !!id });
  if (q.data?.video.runId) return <PageContainer><EngineReview videoId={id!} /></PageContainer>;
  if (q.isLoading) return <PageContainer><TfSpinner /></PageContainer>;
  return <LegacyVideoDetailPage />;
}
